import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import { useToast } from '../components/Toast';
import GlassCard from '../components/GlassCard';
import { AVATARS, generateGroupId, copyToClipboard } from '../lib/utils';

const STEPS = [
  { id: 1, title: "Who are you?", subtitle: "Set up your identity" },
  { id: 2, title: "Your Group", subtitle: "Join or create a study group" },
  { id: 3, title: "Set Your Goal", subtitle: "How much will you study?" },
];

// ── Step indicator ─────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center mb-10">
      {STEPS.map((step, i) => (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 relative"
              style={
                step.id < current
                  ? { background: 'rgba(34,197,94,0.2)', border: '2px solid #22c55e', color: '#22c55e' }
                  : step.id === current
                  ? { background: 'rgba(99,102,241,0.2)', border: '2px solid #6366f1', color: '#818cf8', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }
                  : { background: 'rgba(255,255,255,0.05)', border: '2px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }
              }
            >
              {step.id < current ? '✓' : step.id}
            </div>
            <span className="text-xs mt-1 hidden sm:block"
              style={{ color: step.id === current ? '#818cf8' : 'rgba(255,255,255,0.3)' }}>
              {step.title}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className="w-16 sm:w-24 h-px mx-2 transition-all duration-300"
              style={{ background: step.id < current ? '#22c55e40' : 'rgba(255,255,255,0.1)' }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Avatar Picker ─────────────────────────────────────────────────────────
function AvatarPicker({ selected, onSelect }) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {AVATARS.map((av, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200"
          style={
            selected === i
              ? { background: `${av.bg}20`, border: `2px solid ${av.bg}`, boxShadow: `0 0 16px ${av.bg}40` }
              : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.08)' }
          }
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-transform duration-200 hover:scale-110"
            style={{ background: av.bg }}
          >
            {av.emoji}
          </div>
        </button>
      ))}
    </div>
  );
}

import { useEffect } from 'react';
import { groupsApi } from '../lib/apiClient';

// ── Main Onboarding Page ───────────────────────────────────────────────────
export default function OnboardingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, updateUser, setGroup, fetchUser } = useAuthStore();
  const { createChallenge } = useGroupStore();

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingReq, setPendingReq] = useState(null);
  const [isPendingLoading, setIsPendingLoading] = useState(true);

  // Step 1
  const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar ?? user?.avatarIndex ?? 0);
  const [displayName, setDisplayName] = useState(user?.displayName ?? user?.name ?? '');

  // Step 2
  const [groupTab, setGroupTab] = useState('join'); // 'create' | 'join' | 'browse'
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [generatedId, setGeneratedId] = useState('');
  const [copiedId, setCopiedId] = useState(false);
  const [joinId, setJoinId] = useState(() => {
    const savedCode = sessionStorage.getItem('pendingJoinCode');
    if (savedCode) {
      sessionStorage.removeItem('pendingJoinCode');
      return savedCode;
    }
    return '';
  });
  const [search, setSearch] = useState('');
  const [browseGroups, setBrowseGroups] = useState([]);

  // Step 3
  const [weeklyHours, setWeeklyHours] = useState(user?.weeklyGoalHours ?? 10);
  const [reminderTime, setReminderTime] = useState(user?.reminderTime ?? '09:00');

  // Load pending join request on mount
  useEffect(() => {
    const checkPending = async () => {
      try {
        const res = await groupsApi.getPendingJoinRequest();
        if (res && res.pendingRequest) {
          setPendingReq(res.pendingRequest);
        } else {
          setPendingReq(null);
        }
      } catch (err) {
        console.error('Failed to fetch pending join request status', err);
      } finally {
        setIsPendingLoading(false);
      }
    };
    checkPending();
  }, []);

  // Load public groups dynamically from API
  useEffect(() => {
    let active = true;
    const fetchBrowse = async () => {
      try {
        const res = await groupsApi.list(search).catch(() => null);
        if (active && res) {
          setBrowseGroups(res);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (groupTab === 'browse') {
      fetchBrowse();
    }
    return () => { active = false; };
  }, [search, groupTab]);

  const handleCancelRequest = async () => {
    if (!confirm('Are you sure you want to cancel your join request?')) return;
    setIsLoading(true);
    try {
      await groupsApi.cancelPendingJoinRequest();
      setPendingReq(null);
      toast.success('Join request cancelled.');
    } catch (err) {
      toast.error(err.message || 'Failed to cancel request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setIsLoading(true);
    try {
      await fetchUser();
      const res = await groupsApi.getPendingJoinRequest();
      if (res && res.pendingRequest) {
        setPendingReq(res.pendingRequest);
        toast.success('Still pending. Ask your leader to approve!');
      } else {
        // If no longer pending, they were either approved or rejected.
        const userState = useAuthStore.getState().user;
        if (userState?.groupId) {
          toast.success('🎉 Your request was approved! Welcome to the group.');
          navigate('/dashboard');
        } else {
          setPendingReq(null);
          toast.error('Your request was not accepted or has been resolved. You can join another group.');
        }
      }
    } catch (err) {
      toast.error('Failed to check status');
    } finally {
      setIsLoading(false);
    }
  };

  if (isPendingLoading) {
    return (
      <div className="min-h-screen bg-[#090e1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (pendingReq) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
        style={{ background: 'linear-gradient(135deg, #040812, #090e1a, #0d1426)' }}>
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/3 w-80 h-80 rounded-full opacity-10 blur-3xl"
            style={{ background: 'radial-gradient(circle, #6366f1, transparent)' }} />
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 rounded-full opacity-10 blur-3xl"
            style={{ background: 'radial-gradient(circle, #f59e0b, transparent)' }} />
        </div>

        <div className="relative z-10 w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-8">
            <span className="text-3xl">📖</span>
            <span className="heading text-3xl font-bold text-white tracking-wide">StudyLeague</span>
          </div>

          <GlassCard className="p-8 border border-white/10 shadow-2xl relative overflow-hidden backdrop-blur-md">
            <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-indigo-500/10 blur-xl animate-pulse" />
            <div className="absolute -bottom-10 -left-10 w-28 h-28 rounded-full bg-amber-500/5 blur-xl" />

            <div className="w-16 h-16 bg-indigo-500/10 border border-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl animate-pulse">⏳</span>
            </div>

            <h2 className="heading text-2xl font-bold text-white mb-2">Request Pending</h2>
            <p className="text-white/60 text-sm mb-6">
              Your request to join <strong className="text-indigo-400 font-semibold">{pendingReq.group_name}</strong> is awaiting review.
            </p>

            <div className="bg-white/5 border border-white/5 rounded-xl p-4 mb-6 text-left space-y-3">
              <div className="flex justify-between text-xs">
                <span className="text-white/40 uppercase tracking-wider font-semibold">Group Leader</span>
                <span className="text-white/80 font-medium">{pendingReq.leader_name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40 uppercase tracking-wider font-semibold">Submitted On</span>
                <span className="text-white/80 font-medium">
                  {new Date(pendingReq.created_at * 1000).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="flex justify-between text-xs items-center">
                <span className="text-white/40 uppercase tracking-wider font-semibold">Status</span>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 animate-pulse">
                  Awaiting Approval
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleCheckStatus}
                disabled={isLoading}
                className="btn-primary w-full py-3 font-semibold text-sm transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>🔄 Refresh Status</>
                )}
              </button>
              
              <button
                onClick={handleCancelRequest}
                disabled={isLoading}
                className="btn-secondary w-full py-3 text-red-400 border-red-500/20 hover:bg-red-500/10 hover:border-red-500/30 transition-all duration-300 font-semibold text-sm flex items-center justify-center gap-2"
              >
                Cancel Join Request
              </button>
            </div>
          </GlassCard>

          <p className="text-white/30 text-xs mt-6">
            If the leader takes too long, you can cancel your request to create your own group or join another.
          </p>
        </div>
      </div>
    );
  }

  const handleGenerateId = () => {
    setGeneratedId(generateGroupId());
  };

  const handleCopyId = async () => {
    await copyToClipboard(generatedId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
    toast.success('Group ID copied!');
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!displayName.trim()) { toast.error('Please enter your display name'); return; }
      updateUser({ avatarIndex: selectedAvatar, displayName });
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setIsLoading(true);
      try {
        await updateUser({ weeklyGoalHours: weeklyHours, reminderTime });
        
        let finalGroupId = null;
        if (groupTab === 'create' && newGroupName.trim()) {
          const res = await groupsApi.create({
            name: newGroupName.trim(),
            description: newGroupDesc.trim()
          });
          finalGroupId = res.group?.id || res.id;
        } else if ((groupTab === 'join' || groupTab === 'browse') && joinId.trim()) {
          const res = await groupsApi.join(joinId.trim());
          toast.success(res.message || 'Join request sent. A leader needs to approve it.');
          
          // Re-fetch pending status to render the pending screen!
          const statusRes = await groupsApi.getPendingJoinRequest().catch(() => null);
          if (statusRes && statusRes.pendingRequest) {
            setPendingReq(statusRes.pendingRequest);
          }
          return;
        }

        if (finalGroupId) {
          setGroup(finalGroupId);
          await fetchUser();
          toast.success('🎉 Welcome to StudyLeague!');
          navigate('/dashboard');
        } else {
          toast.error('Please join or create a group first!');
        }
      } catch (err) {
        toast.error(err.message || 'Onboarding failed');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #040812, #090e1a, #0d1426)' }}>
      {/* Background orbs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-80 h-80 rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(circle, #6366f1, transparent)' }} />
      </div>

      <div className="relative z-10 w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-2xl">📖</span>
            <span className="heading text-2xl font-bold text-white">StudyLeague</span>
          </div>
          <p className="text-white/40 text-sm">Let's get you set up</p>
        </div>

        <StepIndicator current={step} />

        <GlassCard className="relative overflow-hidden">
          {/* Step title */}
          <div className="mb-6">
            <h2 className="heading text-2xl font-bold text-white mb-1">{STEPS[step - 1].title}</h2>
            <p className="text-white/50 text-sm">{STEPS[step - 1].subtitle}</p>
          </div>

          {/* ── Step 1: Identity ── */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <label className="text-white/60 text-sm mb-3 block">Choose your avatar</label>
                <AvatarPicker selected={selectedAvatar} onSelect={setSelectedAvatar} />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 block">Display name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="How should we call you?"
                  className="input-glass"
                />
              </div>
            </div>
          )}

          {/* ── Step 2: Group ── */}
          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              {/* Group tabs */}
              <div className="flex gap-2">
                {['create', 'join', 'browse'].map(t => (
                  <button
                    key={t}
                    onClick={() => setGroupTab(t)}
                    className="flex-1 py-2 text-xs font-semibold rounded-lg transition-all duration-200 capitalize"
                    style={groupTab === t
                      ? { background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#818cf8' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }
                    }
                  >
                    {t === 'create' ? '✨ Create' : t === 'join' ? '🔗 Join' : '🔍 Browse'}
                  </button>
                ))}
              </div>

              {groupTab === 'create' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    placeholder="Group name"
                    className="input-glass"
                  />
                  <textarea
                    value={newGroupDesc}
                    onChange={e => setNewGroupDesc(e.target.value)}
                    placeholder="Group description (optional)"
                    className="input-glass resize-none"
                    rows={2}
                  />
                  <button onClick={handleGenerateId} className="btn-secondary w-full text-sm">
                    Generate Group ID
                  </button>
                  {generatedId && (
                    <div className="flex items-center gap-3 p-4 rounded-xl animate-fade-in"
                      style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
                      <code className="text-indigo-400 font-mono text-lg font-bold flex-1">{generatedId}</code>
                      <button onClick={handleCopyId} className="text-indigo-400 hover:text-indigo-300 transition-colors">
                        {copiedId ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {groupTab === 'join' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={joinId}
                    onChange={e => setJoinId(e.target.value.toUpperCase())}
                    placeholder="Enter Group ID (e.g. GRP-7X2K9M)"
                    className="input-glass font-mono tracking-widest"
                    maxLength={10}
                  />
                  <button
                    onClick={() => {
                      if (!joinId.trim()) {
                        toast.error('Enter a Group ID');
                        return;
                      }
                      setStep(3);
                    }}
                    className="btn-primary w-full text-sm font-semibold"
                  >
                    Continue to Goal Setup
                  </button>
                </div>
              )}

              {groupTab === 'browse' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search groups..."
                      className="input-glass pl-10"
                    />
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {browseGroups.map(g => (
                      <button
                        key={g.id}
                        onClick={() => { setJoinId(g.joinCode || g.join_code || g.id); toast.success(`${g.name} selected.`); }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200 lift"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        <div>
                          <div className="text-white font-medium text-sm">{g.name}</div>
                          <div className="text-white/40 text-xs">{g.topic} · {g.members} members · {g.avgHours}h avg/week</div>
                        </div>
                        <ChevronRight size={14} className="text-white/30 ml-auto flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Goals ── */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white/60 text-sm">Weekly study goal</label>
                  <span className="text-indigo-400 font-bold text-lg">{weeklyHours}h</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={40}
                  value={weeklyHours}
                  onChange={e => setWeeklyHours(Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(weeklyHours / 40) * 100}%, rgba(255,255,255,0.1) ${(weeklyHours / 40) * 100}%, rgba(255,255,255,0.1) 100%)`,
                  }}
                />
                <div className="flex justify-between text-white/30 text-xs mt-1">
                  <span>1h</span>
                  <span>40h</span>
                </div>
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">Daily reminder time</label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={e => setReminderTime(e.target.value)}
                  className="input-glass"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              <div className="p-4 rounded-xl"
                style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
                <p className="text-white/60 text-sm">
                  🎯 Studying <strong className="text-indigo-400">{weeklyHours} hours/week</strong> puts you on track
                  to rank in the <strong className="text-amber-400">top 20%</strong> of StudyLeague members.
                </p>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="btn-secondary flex items-center gap-2"
              >
                <ChevronLeft size={16} /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={isLoading}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isLoading ? 'Setting up...' : step === 3 ? '🚀 Enter StudyLeague' : <>Continue <ChevronRight size={16} /></>}
            </button>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
